import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Container,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { ArrowBack, Edit, Visibility, Upgrade, Lock, Close, InfoOutlined } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import SavingOverlay from "../components/SavingOverlay";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useNavigation, useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, typography } from "../theme/designSystem";
import {
  assessmentFormsService,
  assessmentErrorMessage,
  AssessmentFormFamily as Family,
  AssessmentFormVersionRow,
} from "../services/assessmentFormsService";

// Página da família (RN-536d): o histórico de versões de um instrumento. Recebe ?formId= de
// qualquer versão. É aqui que a troca acontece — inativar a que está no ar e ativar a nova,
// já que só uma versão fica ativa por vez.
const AssessmentFormFamilyPage: React.FC = () => {
  const { getParam, navigateTo } = useRouter();
  const { goToDashboard } = useNavigation();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();
  const formId = getParam("formId") || "";

  const [family, setFamily] = useState<Family | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [versionTarget, setVersionTarget] = useState<AssessmentFormVersionRow | null>(null);
  const [versionTitle, setVersionTitle] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setFamily(await assessmentFormsService.getFamily(formId));
    } catch (e) {
      setError(assessmentErrorMessage(e, "Erro ao carregar as versões do formulário."));
    } finally {
      setLoading(false);
    }
  }, [formId]);

  useEffect(() => { load(); }, [load]);

  const run = async (action: () => Promise<unknown>, ok: string, fail: string) => {
    setSaving(true);
    try {
      await action();
      showToast(ok, "success");
      await load();
      return true;
    } catch (e) {
      showToast(assessmentErrorMessage(e, fail), "error");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = (version: AssessmentFormVersionRow) =>
    run(
      () => assessmentFormsService.update(version.id, {
        title: version.title,
        description: version.description,
        allowPartialCompletion: version.allowPartialCompletion,
        calcTypeId: version.calcTypeId,
        active: !version.active,
      }),
      version.active ? "Versão inativada." : "Versão ativada.",
      version.active ? "Erro ao inativar a versão." : "Erro ao ativar a versão.",
    );

  const handleNewVersion = async () => {
    if (!versionTarget) return;
    const ok = await run(
      () => assessmentFormsService.newVersion(versionTarget.id, versionTitle),
      "Nova versão criada (inativa).",
      "Erro ao criar a nova versão.",
    );
    if (ok) setVersionTarget(null);
  };

  const preview = (id: string) =>
    window.open(`${window.location.origin}/?page=assessment-application&formId=${id}`, "_blank");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" sx={{ padding: "1rem", minHeight: "calc(100vh - 120px)", background: colors.background, marginTop: "85px", flex: 1 }}>
        <Container maxWidth={false} disableGutters>
          <Paper sx={{ p: 2, borderRadius: "12px", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Button
                onClick={() => navigateTo("assessment-forms")}
                startIcon={<ArrowBack />}
                sx={{ textTransform: "none", color: colors.primary, minWidth: 0 }}
              >
                Formulários
              </Button>
              <Typography variant="h4" sx={{ fontSize: "1.3rem", fontWeight: typography.fontWeight.semibold, color: colors.textPrimary }}>
                {loading ? "Carregando..." : family?.title}
              </Typography>
              {family && (
                <Chip
                  label={`${family.versions.length} versão${family.versions.length === 1 ? "" : "ões"}`}
                  size="small"
                  sx={{ height: 22, fontSize: "0.72rem" }}
                />
              )}
            </Box>
            <Box className="c4u-info-box">
              <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
              <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textPrimary }}>
                {family && family.versions.length === 1 ? (
                  <>
                    Este instrumento tem <strong>uma única versão</strong> — nada está sendo filtrado aqui.
                    Ao criar uma nova versão, ela aparece nesta página, inativa, para você revisar antes
                    de colocá-la no ar (só uma versão fica ativa por vez).
                  </>
                ) : (
                  <>
                    Versões do mesmo instrumento, da mais nova para a mais antiga — ativas e inativas.
                    <strong> Só uma fica ativa por vez</strong>: para colocar uma nova no ar, inative a que
                    está ativa e então ative a nova. Avaliações já aplicadas continuam ligadas à versão em
                    que foram respondidos.
                  </>
                )}
              </Typography>
            </Box>
          </Paper>

          {error && (
            <Typography sx={{ color: colors.error, fontSize: typography.fontSize.sm, mb: 2 }}>{error}</Typography>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {(family?.versions ?? []).slice().reverse().map((version) => (
                <Paper key={version.id} sx={{ p: 1.5, borderRadius: "12px" }}>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                        <Typography sx={{ fontWeight: 600, color: colors.textPrimary, fontSize: "0.95rem" }}>
                          v{version.version} — {version.title}
                        </Typography>
                        {version.locked && (
                          <Tooltip title="Já aplicada a paciente: estrutura imutável" arrow>
                            <Lock sx={{ fontSize: "0.95rem", color: colors.textSecondary }} />
                          </Tooltip>
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ color: colors.textSecondary, display: "block" }}>
                        {version.sectionsCount} seções · {version.questionsCount} itens ·{" "}
                        {version.applicationsCount} aplicações · criada em{" "}
                        {`${version.createdAt.slice(8, 10)}/${version.createdAt.slice(5, 7)}/${version.createdAt.slice(0, 4)}`}
                        {version.calcTypeName ? ` · Resultado: ${version.calcTypeName}` : ""}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Tooltip title={version.active ? "Ativa: em uso nas novas aplicações" : "Inativa: não aparece para aplicar"} arrow>
                        <Chip
                          label={version.active ? "Ativa" : "Inativa"}
                          size="small"
                          onClick={() => toggleActive(version)}
                          sx={{
                            cursor: "pointer", color: "#fff", fontWeight: 600,
                            backgroundColor: version.active ? "#28a745" : "#adb5bd",
                            "&:hover": { opacity: 0.85 },
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Pré-visualizar: ver o questionário como o profissional verá (nada é gravado)" arrow>
                        <IconButton
                          size="small"
                          onClick={() => preview(version.id)}
                          sx={{ color: colors.primary, border: "1px solid #e0f5f7", width: 32, height: 32, "&:hover": { backgroundColor: "#e0f5f7" } }}
                        >
                          <Visibility sx={{ fontSize: "1.1rem" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Nova versão a partir desta (nasce inativa)" arrow>
                        <IconButton
                          size="small"
                          onClick={() => { setVersionTarget(version); setVersionTitle(`${version.title} — v${version.version + 1}`); }}
                          sx={{ color: colors.primary, border: "1px solid #e0f5f7", width: 32, height: 32, "&:hover": { backgroundColor: "#e0f5f7" } }}
                        >
                          <Upgrade sx={{ fontSize: "1.1rem" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Abrir editor" arrow>
                        <IconButton
                          size="small"
                          onClick={() => navigateTo("assessment-form-editor", { formId: version.id })}
                          sx={{ color: colors.primary, border: "1px solid #e0f5f7", width: 32, height: 32, "&:hover": { backgroundColor: "#e0f5f7" } }}
                        >
                          <Edit sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />

      <Dialog open={versionTarget !== null} onClose={() => !saving && setVersionTarget(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}>
        <SavingOverlay open={saving} message="Criando versão..." />
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            Nova versão
          </Typography>
          <IconButton onClick={() => setVersionTarget(null)} disabled={saving} sx={{ color: colors.white, padding: "0.25rem" }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          <TextField
            label="Título da nova versão"
            size="small"
            value={versionTitle}
            onChange={(e) => setVersionTitle(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: 255 }}
            fullWidth
          />
          <Typography variant="body2" sx={{ color: colors.textSecondary, mt: 1 }}>
            Cópia completa da v{versionTarget?.version} na mesma família. Nasce <strong>inativa</strong>:
            revise, inative a versão que está no ar e então ative esta.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button onClick={() => setVersionTarget(null)} variant="outlined" sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}>Cancelar</Button>
          <Button variant="contained" disabled={!versionTitle.trim() || saving} onClick={handleNewVersion} sx={{ textTransform: "none", backgroundColor: colors.primary }}>
            Criar nova versão
          </Button>
        </DialogActions>
      </Dialog>

      <SavingOverlay open={saving && versionTarget === null} message="Salvando..." />
      <Toast isVisible={toast.isVisible} message={toast.message} type={toast.type} onClose={hideToast} />
    </Box>
  );
};

export default AssessmentFormFamilyPage;
